import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useEffect } from 'react';

export type MapStyle = 'osm' | 'osm-humanitarian' | 'google-streets' | 'google-traffic' | 'google-satellite' | 'google-hybrid';
export type Theme = 'light' | 'dark' | 'system';

export interface MapStyleOption {
  id: MapStyle;
  name: string;
  description: string;
  preview?: string;
}

export interface ThemeOption {
  id: Theme;
  name: string;
  description: string;
  icon: 'sun' | 'moon' | 'monitor';
}

export const MAP_STYLES: MapStyleOption[] = [
  { id: 'osm', name: 'OpenStreetMap', description: 'Carte standard OpenStreetMap' },
  { id: 'osm-humanitarian', name: 'OSM Humanitarian', description: 'Style humanitaire avec plus de détails' },
  { id: 'google-streets', name: 'Google Maps', description: 'Carte routière Google' },
  { id: 'google-traffic', name: 'Google Traffic', description: 'Carte Google avec trafic en temps réel' },
  { id: 'google-satellite', name: 'Google Satellite', description: 'Vue satellite Google' },
  { id: 'google-hybrid', name: 'Google Hybrid', description: 'Vue satellite avec noms des rues' },
];

export const THEME_OPTIONS: ThemeOption[] = [
  { id: 'light', name: 'Clair', description: 'Theme lumineux', icon: 'sun' },
  { id: 'dark', name: 'Sombre', description: 'Theme sombre', icon: 'moon' },
  { id: 'system', name: 'Système', description: 'Suivre les préférences du système', icon: 'monitor' },
];

// Refresh interval options (in seconds)
export const REFRESH_INTERVALS = [
  { value: 10, label: '10 secondes', batterySaver: false },
  { value: 30, label: '30 secondes', batterySaver: false },
  { value: 60, label: '1 minute', batterySaver: true },
  { value: 120, label: '2 minutes', batterySaver: true },
  { value: 300, label: '5 minutes', batterySaver: true },
];

interface SettingsState {
  mapStyle: MapStyle;
  theme: Theme;
  // Battery saver settings
  batterySaverMode: boolean;
  refreshInterval: number; // in seconds
  reduceAnimations: boolean;
  reduceMapQuality: boolean;
  // Actions
  setMapStyle: (style: MapStyle) => void;
  setTheme: (theme: Theme) => void;
  setBatterySaverMode: (enabled: boolean) => void;
  setRefreshInterval: (interval: number) => void;
  setReduceAnimations: (enabled: boolean) => void;
  setReduceMapQuality: (enabled: boolean) => void;
  // Computed
  getEffectiveRefreshInterval: () => number;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      mapStyle: 'osm',
      theme: 'light',
      batterySaverMode: false,
      refreshInterval: 30,
      reduceAnimations: false,
      reduceMapQuality: false,

      setMapStyle: (mapStyle) => set({ mapStyle }),
      setTheme: (theme) => set({ theme }),
      setBatterySaverMode: (batterySaverMode) => {
        set({ batterySaverMode });
        // Auto-adjust settings when battery saver is enabled
        if (batterySaverMode) {
          const currentInterval = get().refreshInterval;
          if (currentInterval < 60) {
            set({ refreshInterval: 60 });
          }
          set({ reduceAnimations: true });
        }
      },
      setRefreshInterval: (refreshInterval) => set({ refreshInterval }),
      setReduceAnimations: (reduceAnimations) => set({ reduceAnimations }),
      setReduceMapQuality: (reduceMapQuality) => set({ reduceMapQuality }),

      getEffectiveRefreshInterval: () => {
        const state = get();
        if (state.batterySaverMode && state.refreshInterval < 60) {
          return 60;
        }
        return state.refreshInterval;
      },
    }),
    {
      name: 'trackfleet-settings',
    }
  )
);

// Hook to apply theme to document
export function useTheme() {
  const { theme } = useSettingsStore();

  useEffect(() => {
    const root = document.documentElement;

    const applyTheme = (isDark: boolean) => {
      if (isDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      applyTheme(mediaQuery.matches);

      const handler = (e: MediaQueryListEvent) => applyTheme(e.matches);
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      applyTheme(theme === 'dark');
    }
  }, [theme]);

  return theme;
}
